import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { get } from '../controllers/apiController';

const NotificationContext = createContext({});

const POLL_INTERVAL = 60 * 1000; // 60 saniye

const formatReminderTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = d - now;
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 0) return 'Geçti';
    if (diffMin === 0) return 'Şimdi';
    if (diffMin < 60) return `${diffMin} dakika sonra`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH} saat sonra`;
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadWA, setUnreadWA] = useState(0);
    const [totalBadge, setTotalBadge] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    // Daha önce toast gösterilen ID'leri hatırlamak için
    const shownReminders = useRef(new Set());

    const fetchNotifications = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const r = await get('notifications');
            if (!r) return;

            const allReminders = [
                ...(r.overdueReminders || []).map(rem => ({ ...rem, type: 'overdue' })),
                ...(r.reminders || []).map(rem => ({ ...rem, type: rem.urgency || 'upcoming' })),
            ];

            setNotifications(allReminders);
            setUnreadWA(r.unreadWhatsapp || 0);

            const badge = allReminders.length + (r.unreadWhatsapp > 0 ? 1 : 0);
            setTotalBadge(badge);

            // Acil hatırlatmalar için toast göster (ilk kez)
            if (hasLoaded) {
                (r.urgentReminders || []).forEach(rem => {
                    const key = `rem_${rem.Form_ID}_${rem.Hatirlatma_Tarihi}`;
                    if (!shownReminders.current.has(key)) {
                        shownReminders.current.add(key);
                        toast.warning(
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '13px' }}>
                                    ⏰ Hatırlatma: {rem.Adi_Soyadi}
                                </div>
                                <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                                    {formatReminderTime(rem.Hatirlatma_Tarihi)}
                                    {rem.Aciklama ? ` — ${rem.Aciklama}` : ''}
                                </div>
                            </div>,
                            {
                                autoClose: 8000,
                                onClick: () => window.open(`./lead-detail?id=${rem.Form_ID}`, '_self')
                            }
                        );
                    }
                });

                // Yeni okunmamış WA için toast — sadece sayı artınca göster
                const shownCount = parseInt(sessionStorage.getItem('wa_shown_count') || '0', 10);
                if (r.unreadWhatsapp > 0 && r.unreadWhatsapp > shownCount) {
                    sessionStorage.setItem('wa_shown_count', String(r.unreadWhatsapp));
                    toast.info(
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '13px' }}>
                                💬 {r.unreadWhatsapp} okunmamış WhatsApp mesajı
                            </div>
                            <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                                Gelen kutunuzu kontrol edin
                            </div>
                        </div>,
                        {
                            autoClose: 6000,
                            onClick: () => window.open('./whatsapp-inbox', '_self')
                        }
                    );
                }
                if (r.unreadWhatsapp === 0) sessionStorage.removeItem('wa_shown_count');
            }

            setHasLoaded(true);
        } catch {
            // sessiz hata — bildirim yoksa uygulama çalışmaya devam eder
        }
    }, [hasLoaded]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const dismiss = useCallback((formId) => {
        setNotifications(prev => prev.filter(n => n.Form_ID !== formId));
        setTotalBadge(prev => Math.max(0, prev - 1));
    }, []);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadWA,
            totalBadge,
            isOpen,
            setIsOpen,
            dismiss,
            refresh: fetchNotifications,
            formatReminderTime
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
